import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { t } from "../../lib/i18n";

import "./ui.css";

export interface TypeaheadOption {
  id: string;
  label: string;
  /** Optional secondary text (e.g. kind, badge) */
  meta?: ReactNode;
  /** Excluded from selection (e.g. already a member). */
  disabled?: boolean;
  /** Reason shown next to disabled options. */
  disabledReason?: string;
}

interface TypeaheadPickerProps {
  /** All candidates. The picker filters in-memory using a case-insensitive substring match. */
  options: TypeaheadOption[];
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  placeholder?: string;
  /** Render an extra row when no option matches the search (e.g. "Add new"). */
  onCreate?: (query: string) => void;
  createLabel?: (query: string) => string;
  /** Allow only single selection. */
  single?: boolean;
  /**
   * Controlled query. When provided, the parent owns the search string and
   * is expected to drive any server-side filtering. Pair with
   * `onQueryChange` to receive keystrokes.
   */
  query?: string;
  onQueryChange?: (next: string) => void;
  /** Render a loading row inside the dropdown (e.g. while server search runs). */
  loading?: boolean;
}

export function TypeaheadPicker({
  options,
  selectedIds,
  onChange,
  placeholder,
  onCreate,
  createLabel,
  single = false,
  query: queryProp,
  onQueryChange,
  loading = false,
}: TypeaheadPickerProps) {
  const isControlled = queryProp !== undefined;
  const [queryState, setQueryState] = useState("");
  const query = isControlled ? queryProp : queryState;
  const setQuery = (next: string) => {
    if (!isControlled) setQueryState(next);
    onQueryChange?.(next);
  };
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [query, options]);

  const isExactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return options.some((o) => o.label.toLowerCase() === q);
  }, [query, options]);

  function toggle(id: string) {
    if (single) {
      onChange(new Set([id]));
      setOpen(false);
      return;
    }
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  }

  function removeChip(id: string) {
    const next = new Set(selectedIds);
    next.delete(id);
    onChange(next);
  }

  const selectedOptions = options.filter((o) => selectedIds.has(o.id));

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          padding: 6,
          minHeight: 38,
          border: "1px solid var(--color-border-strong)",
          borderRadius: 6,
          background: "var(--color-surface)",
          cursor: "text",
        }}
        onClick={() => setOpen(true)}
      >
        {selectedOptions.map((opt) => (
          <span
            key={opt.id}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "2px 8px",
              borderRadius: 100,
              background: "var(--color-accent-soft)",
              border: "1px solid var(--color-accent-soft-strong)",
              color: "var(--color-accent)",
              fontSize: 12,
              fontFamily: "var(--font-mono)",
            }}
          >
            {opt.label}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeChip(opt.id);
              }}
              style={{
                background: "transparent",
                border: 0,
                color: "currentColor",
                cursor: "pointer",
                padding: 0,
                lineHeight: 1,
                fontSize: 14,
              }}
              aria-label="Remove"
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={query}
          placeholder={selectedOptions.length === 0 ? placeholder ?? t("common.search") : ""}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          style={{
            flex: "1 1 100px",
            minWidth: 120,
            border: 0,
            outline: "none",
            background: "transparent",
            fontSize: 13,
          }}
        />
      </div>
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 40,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-strong)",
            borderRadius: 6,
            maxHeight: 280,
            overflow: "auto",
            boxShadow: "var(--shadow-lg)",
            padding: 4,
          }}
        >
          {loading && (
            <div style={{ padding: 12, fontSize: 12, color: "var(--color-text-muted)" }}>
              {t("common.loading")}
            </div>
          )}
          {!loading && filtered.length === 0 && !onCreate && (
            <div style={{ padding: 12, fontSize: 12, color: "var(--color-text-muted)" }}>
              {t("common.no")}
            </div>
          )}
          {filtered.map((opt) => {
            const isSelected = selectedIds.has(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={opt.disabled}
                onClick={() => toggle(opt.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "6px 10px",
                  border: 0,
                  borderRadius: 4,
                  background: isSelected ? "var(--color-accent-soft)" : "transparent",
                  textAlign: "left",
                  cursor: opt.disabled ? "not-allowed" : "pointer",
                  font: "inherit",
                  opacity: opt.disabled ? 0.5 : 1,
                }}
              >
                <span style={{ flex: 1 }}>{opt.label}</span>
                {opt.meta && (
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--color-text-muted)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {opt.meta}
                  </span>
                )}
                {opt.disabled && opt.disabledReason && (
                  <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
                    {opt.disabledReason}
                  </span>
                )}
                {isSelected && !opt.disabled && (
                  <span style={{ color: "var(--color-accent)", fontWeight: 600 }}>✓</span>
                )}
              </button>
            );
          })}
          {onCreate && query.trim() && !isExactMatch && (
            <button
              type="button"
              onClick={() => {
                onCreate(query.trim());
                setQuery("");
              }}
              style={{
                display: "flex",
                width: "100%",
                padding: "8px 10px",
                marginTop: 4,
                border: "1px dashed var(--color-border-strong)",
                borderRadius: 4,
                background: "transparent",
                color: "var(--color-accent)",
                textAlign: "left",
                cursor: "pointer",
                font: "inherit",
                fontSize: 12.5,
              }}
            >
              {createLabel ? createLabel(query.trim()) : `+ ${query.trim()}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
